/**
 * Holding the whole wall on the screen, by type size rather than by shrinking
 * the tiles.
 *
 * The arithmetic is in `terminal/fitToScreen.ts` and is pure. This is the part
 * that cannot be: measuring the box, reading the floor out of the stylesheet,
 * and writing the one custom property every open pane is watching.
 *
 * **Why it writes `--k-term-font` on the root and nothing else.** That property
 * is already the single place the type size lives: `TerminalPane` observes the
 * root's `style` attribute and re-fits and re-announces on any change, the
 * readability floor is derived from it, and the settings panel writes it the
 * same way. A second mechanism — a class, a context, a prop threaded to every
 * pane — would be a second answer to "how big is a cell", and the floor can
 * only be honest if there is one.
 *
 * **It does not touch the stored preference.** `kimene.termFont` stays whatever
 * the operator chose; this is a view state, like the layout mode, and turning
 * the control off puts their size back. That also makes the ceiling meaningful:
 * the fit is only ever allowed to make type *smaller* than they asked for.
 *
 * **Every recompute costs a fleet-wide resize**, because each pane answers the
 * property change by re-fitting and announcing new geometry to tmux, which
 * repaints the far side. So the scan runs in full before anything is written,
 * the write is skipped when the answer has not moved, and the measurement is
 * debounced — without that, dragging the column splitter would send a resize
 * storm to every agent on the wall.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { fitFont, readFloorMetrics, FONT_FLOOR, type Fit } from '@/terminal/fitToScreen'

/** Where the appearance panel keeps the operator's own size. */
const STORED = 'kimene.termFont'

/** The same bounds `readTermFontSize` and `applyStoredAppearance` enforce. A
 *  stored value outside them is a value somebody edited by hand. */
function storedCeiling(): number {
  const raw = Number.parseFloat(localStorage.getItem(STORED) ?? '')
  return Number.isFinite(raw) && raw >= FONT_FLOOR && raw <= 24 ? raw : 12.5
}

/** A resize this settles under is a resize nobody saw; a resize it does not is
 *  a drag, and a drag must not become one tmux resize per frame. */
const SETTLE_MS = 160

/** How much the box has to move before the answer is worth recomputing. One
 *  scrollbar's worth, plus a little: see `measured` below for the loop this
 *  exists to break. */
const DEAD_BAND = 20

export interface FitToScreen {
  /** What the wall is currently fitted to, or null while the control is off. */
  fit: Fit | null
}

export function useFitToScreen({
  enabled,
  cells,
  box,
  grid,
}: {
  enabled: boolean
  /** Grid cells the wall occupies — a tile spanning two columns takes two. */
  cells: number
  /** The element whose *content* box the tiles have to fit inside. */
  box: HTMLElement | null
  /** The grid itself, which is where the gap and the floor tokens resolve. */
  grid: HTMLElement | null
}): FitToScreen {
  const [fit, setFit] = useState<Fit | null>(null)
  /** What this hook last wrote, so an outside write can be told from an echo of
   *  its own. Without it the root observer below would answer every change it
   *  caused and the two would chase each other. */
  const applied = useRef<string | null>(null)
  /** The box the current answer was computed against, and the reason there is a
   *  dead band below.
   *
   *  A successful fit removes the wall's scrollbar, which widens the box by
   *  about fifteen pixels, which may admit a larger type size, which brings the
   *  scrollbar back. That is a loop, and on this screen each turn of it is a
   *  tmux resize on every pane. A resize smaller than a scrollbar is therefore
   *  not a resize worth answering; anything a person did to the window clears
   *  it easily. */
  const measured = useRef<{ width: number; height: number } | null>(null)

  const restore = useCallback(() => {
    applied.current = null
    measured.current = null
    const stored = localStorage.getItem(STORED)
    const n = Number.parseFloat(stored ?? '')
    if (Number.isFinite(n) && n >= FONT_FLOOR && n <= 24) {
      document.documentElement.style.setProperty('--k-term-font', String(n))
    } else {
      // Nothing stored means the operator never chose: the token's own default
      // is the right answer, and removing the inline property is how to get
      // back to it. Writing 12.5 would look identical and quietly turn a
      // default into a choice.
      document.documentElement.style.removeProperty('--k-term-font')
    }
  }, [])

  const recompute = useCallback(
    (force: boolean) => {
    if (!box || !grid) return
    const metrics = readFloorMetrics(grid)
    // No metrics means the stylesheet did not answer — a token renamed, a test
    // environment with no computed style. Moving every terminal to a size
    // derived from a guess is worse than leaving them where they are.
    if (!metrics) return
    const cs = getComputedStyle(box)
    // `clientWidth`, not the bounding rect: the rect counts the scrollbar the
    // overflowing wall is drawing, and the tiles do not get to lay out in it.
    const width = box.clientWidth - Number.parseFloat(cs.paddingLeft) - Number.parseFloat(cs.paddingRight)
    const height = box.clientHeight - Number.parseFloat(cs.paddingTop) - Number.parseFloat(cs.paddingBottom)
    if (!(width > 0) || !(height > 0)) return
    const was = measured.current
    if (!force && was && Math.abs(was.width - width) < DEAD_BAND && Math.abs(was.height - height) < DEAD_BAND) {
      return
    }
    measured.current = { width, height }

    const next = fitFont({ box: { width, height }, cells, metrics, ceiling: storedCeiling() })
    setFit(next)
    const value = String(next.font)
    if (applied.current === value) return
    applied.current = value
    document.documentElement.style.setProperty('--k-term-font', value)
    },
    [box, grid, cells],
  )

  useEffect(() => {
    if (!enabled) {
      setFit(null)
      restore()
      return
    }
    let timer: ReturnType<typeof setTimeout> | null = null
    const settle = (force: boolean) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => recompute(force), SETTLE_MS)
    }

    // Immediately on the way in, and forced: the operator pressed a button and
    // expects the wall to move, not to move once they also resize something.
    recompute(true)

    const observers: Array<{ disconnect: () => void }> = []
    if (box && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => settle(false))
      ro.observe(box)
      observers.push(ro)
    }
    // The appearance panel changing the size while this is on: the ceiling moved,
    // so the answer has to be recomputed against it. Guarded on the value being
    // one this hook did not write, or it would answer its own writes forever.
    const mo = new MutationObserver(() => {
      const now = document.documentElement.style.getPropertyValue('--k-term-font').trim()
      if (now !== '' && now === applied.current) return
      settle(true)
    })
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'data-density'] })
    observers.push(mo)

    return () => {
      if (timer) clearTimeout(timer)
      for (const o of observers) o.disconnect()
    }
  }, [enabled, recompute, restore, box])

  // Leaving the screen with the wall still fitted would carry a 9px terminal
  // into every other screen in the console, including the next one the operator
  // opens in this tab.
  useEffect(() => () => restore(), [restore])

  return { fit }
}
